import {
  Component,
  EventEmitter,
  OnInit,
  Input,
  Output,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy
} from "@angular/core";
import { IonGrid, Platform } from "@ionic/angular";
import { ButtonnVirtualKeypadStyle } from "src/model/interface/button-virtual-keypad-style";

@Component({
    selector: "app-numeric-keypad",
    templateUrl: "./numeric-keypad.component.html",
    styleUrls: ["./numeric-keypad.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NumericKeypadComponent implements OnInit, OnDestroy {
  private _longPressDeleteRunningTimer: any = null;

  public buttonCssStyle: any = null;
  public colCssStyle: any = null;
  public lastPressedButtonValue: string = "";
  public passwordEncrypted = "";
  public passwordString: string = "";

  public buttonAction: any = {
    DELETE: "DELETE",
    SUBMIT: "SUBMIT",
  };

  @Input("style") containerStyle: any;
  @Input("buttonStyle") buttonStyleObject: ButtonnVirtualKeypadStyle;
  @Input("initialValue") initialValue: string; //default initial component value
  @Input("locked") locked: boolean = false; //it's used to lock component iteratction
  @Input("maxCodeLength") maxCodeLength: number; //Max code length

  @Output() onPasswordSubmission = new EventEmitter<string>();
  @Output() onReleaseButtonNumeric = new EventEmitter<string>(); //paswrod change output event
  @Output() onPasswordChange = new EventEmitter<string>(); //paswrod change output event

  @ViewChild("gridRef", { static: true }) gridRef: ElementRef;

  @HostListener('mousedown', ['$event']) onMouseDown(event: any) {

    this.stopClickPressEventPropagation(event);

  }

  constructor (private _platform: Platform) { }

  ngOnInit() {

    this.generateComponentClasses();


    this.initialValue = this.initialValue || "";

    this.setInitialComponentValue();
  }


  ngOnChanges() {

    this.initialValue = this.initialValue || "";

    this.setInitialComponentValue();
  }


  ngOnDestroy() { }

  /**
   * Handle click number press button
   *
   * @param numberPressed Component number pressed
   */
  public onClickPressNumber(event: any, numberPressed: string): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.passwordEncrypted.length < this.maxCodeLength) {
      this.lastPressedButtonValue = numberPressed;
      this.passwordString += numberPressed;
      this.passwordEncrypted += "*";

      this.onPasswordChange.emit(this.passwordString);
    }
  }

  /**
   * Handle release single button action
   *
   * @param event
   */
  public onReleaseNumber(event: any): void {

    event.stopPropagation();

    this.lastPressedButtonValue = "";
    this.onReleaseButtonNumeric.emit();
  }

  /**
   * Handle click code submission and reset single value field (password and own component value displayed)
   */
  public onClickSubmitCode(event: any): void {

    event.stopPropagation();
    event.preventDefault();

    this.lastPressedButtonValue = this.buttonAction.SUBMIT;

    this.onPasswordSubmission.emit(this.passwordString);

    setTimeout(() => {
      this.resetPasswordFields();
    }, 1000);
  }

  /**
   * Handle release submit button
   */
  public onReleaseSubmit($event): void {

    this.lastPressedButtonValue = "";
  }

  /**
   * Mouse down event handler from desktop/web
   */
  public MouseDown(event: any): void {
    // otherwise on mobile device the command is send 2 times
    if (!this._platform.is("ios") && !this._platform.is("android")) {
      this.onClickDeleteButton(event);
    }
  }

  /**
   * GF: handle the touch from device
   */
  public TouchStart(event: any): void {
    this.onClickDeleteButton(event);
  }

  /**
   * Mouse up event handler on desktop/web
   */
  public MouseUp(event: any): void {
    if (!this._platform.is("ios") && !this._platform.is("android")) {
      this.onReleaseDeleteButton(event);
    }
  }

  /**
   * Mouse up event handler on device
   */
  public TouchEnd(event: any): void {
    this.onReleaseDeleteButton(event);
  }



  /**
   * This method intecept any click/mouseDown event on this particular component, when this event has NOT already 
   * been intercepted by the native eventListeners on single buttons. 
   * This is intended to make so that any click/tap on any part of the component doesn't trigger the global 
   * "clickListener" used to trigger the exit from KioskMode.
   * @param event 
   */
  private stopClickPressEventPropagation(event: any): void {
    //if (this.passwordEncrypted.length === 0 || this.locked) {
    event.stopPropagation();
    event.preventDefault();
    // }
  }

  /**
   * Handle click delete button
   */
  private onClickDeleteButton(event: any): void {

    event.stopPropagation();
    event.preventDefault();

    this.lastPressedButtonValue = this.buttonAction.DELETE;

    if (this.passwordString.length > 0) {
      this.passwordString = this.passwordString.substring(
        0,
        this.passwordString.length - 1
      );
      this.passwordEncrypted = this.passwordEncrypted.substring(
        0,
        this.passwordEncrypted.length - 1
      );

      this.handleLogPressDeleteTimer();
    }
  }

  /**
   * Handle release delete button
   *
   * @param event
   */
  private onReleaseDeleteButton(event: any): void {

    event.stopPropagation();
    this.lastPressedButtonValue = "";

    this.clearLongPressDeleteTimer();
  }

  /**
   * Set initial compoentn value by input password value
   */
  private setInitialComponentValue(): void {

    this.maxCodeLength = Number((this.maxCodeLength ??= 999));

    this.passwordString = this.initialValue;
    this.passwordEncrypted = this.initialValue.replace(/./gi, "*");

  }

  /**
   * Handle long press delete button procedure
   */
  private handleLogPressDeleteTimer(): void {
    this.clearLongPressDeleteTimer();

    this._longPressDeleteRunningTimer = setTimeout(() => {
      this.resetPasswordFields();
    }, 1000);
  }

  /**
   * Cancel (if existing) running timer for long press action
   */
  private clearLongPressDeleteTimer(): void {
    if (this._longPressDeleteRunningTimer) {
      clearInterval(this._longPressDeleteRunningTimer);

      this._longPressDeleteRunningTimer = null;
      this.lastPressedButtonValue = "";
    }
  }

  /**
   * Reset password fields and reset last button clicked variable
   */
  private resetPasswordFields(): void {
    this.passwordString = "";
    this.passwordEncrypted = "";
    this.lastPressedButtonValue = "";

    this.onPasswordChange.emit(this.passwordString);
  }

  /**
   * Generate keypad component CSS class and style for nested element
   */
  private generateComponentClasses(): void {
    this.buttonCssStyle = this.normalizeButtonStyleObject();
  }

  /**
   * Normalize input style object and reassign to CSS property button.
   * It also handle fallback to default button style property
   *
   * @returns configuration object
   */
  private normalizeButtonStyleObject(): object {
    const borderRadius: number = this.buttonStyleObject?.borderRadius || 0;
    const background: string =
      this.buttonStyleObject?.backgroundColor || "var(--ion-color-primary )";
    const color: string = this.buttonStyleObject?.textColor || "#FFFFFF";
    const { width, height } = this.calculateButtonSize();

    return {
      "border-radius": borderRadius + "px",
      "--border-radius": borderRadius + "px",
      "--background": background,
      background,
      color,
      width,
      height,
    };
  }

  /**
   * Calculate button size by input stretch property.
   * If stretch property is true, button fill all container avaiable,
   * otherwiser calculate button size
   *
   * @returns
   */
  private calculateButtonSize(): any {
    let buttonWidth: string | number = "100%";
    let buttonHeight: string | number = "100%";
    let isButtonStretch: boolean = this.buttonStyleObject?.stretch || false;

    if (isButtonStretch) {
      buttonWidth = (this.gridRef.nativeElement.clientWidth - 60) / 3; //60 is value of left/right padding calculate from sum of each button
      buttonHeight =
        (this.gridRef.nativeElement.clientHeight - 80) / //80 is value of top/bottom padding calculate from sum of each button
        4;

      const minWidthHeigth = Math.min(buttonWidth, buttonHeight);

      buttonHeight = minWidthHeigth + "px";
      buttonWidth = minWidthHeigth + "px";
    }

    return {
      width: buttonWidth,
      height: buttonHeight,
    };
  }
}
